import type {
  PlaygroundCheck,
  PlaygroundModel,
  PlaygroundOptions,
  PlaygroundPrompts,
  PlaygroundRun,
  StartPlaygroundRun,
} from '@api/demand-playground';

/** How often a run in progress is asked for (ZLAYT-1660). */
export const POLL_MS = 10000;

const IN_PROGRESS: readonly string[] = ['running', 'scoring'];

export const isRunInProgress = (status: string | undefined): boolean =>
  !!status && IN_PROGRESS.includes(status);

/** Some letter of the run is still being written or checked. */
export const anyLetterRunning = (run: PlaygroundRun): boolean =>
  [run.letters?.new, run.letters?.current].some(
    (letter) => letter?.status === 'running'
  );

/**
 * The SWR refresh interval for a run: every POLL_MS while it is being
 * written or scored, or while any of its letters is still running (whatever
 * the run's own status says), then never again. A run not loaded yet is
 * still polled.
 */
export const pollInterval = (run: PlaygroundRun | undefined): number =>
  !run || isRunInProgress(run.status) || anyLetterRunning(run) ? POLL_MS : 0;

export const httpStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

/** A 404 or 403 means the playground is off for this user; it will not change. */
export const isOffError = (err: unknown): boolean =>
  [403, 404].includes(httpStatus(err) ?? 0);

export const FAILED_MESSAGE = 'Something went wrong. Try again.';

/**
 * The message for a failed request: the server's own words for a bad
 * request (its {"detail"} or the app's {"error": {"message"}} shape).
 */
export function errorText(err: unknown): string {
  const status = httpStatus(err);
  if (status === 404 || status === 403) {
    return 'The test page is not available to you.';
  }
  if (status === 400 || status === 429) {
    const data = (err as { response?: { data?: any } } | null)?.response?.data;
    if (typeof data?.detail === 'string' && data.detail) return data.detail;
    if (typeof data?.error?.message === 'string' && data.error.message) {
      return data.error.message;
    }
  }
  if (status === 429) return 'Too many tries for now. Try again later.';
  return FAILED_MESSAGE;
}

/**
 * Staff only: the user must be a superuser and the server must say the
 * playground is on. The server refuses everyone else (404) as well.
 */
export const canUsePlayground = (
  user: { is_superuser?: boolean } | null | undefined,
  options: PlaygroundOptions | undefined
): boolean => user?.is_superuser === true && options?.enabled === true;

/** The model marked current, else the first, else none. */
export const defaultModel = (models: PlaygroundModel[]): string =>
  (models.find((m) => m.current) ?? models[0])?.id ?? '';

/** A model's label from the options, else its id. */
export const modelLabel = (
  id: string,
  models: PlaygroundModel[] | undefined
): string => models?.find((m) => m.id === id)?.label ?? id;

/** A deep copy of a prompt set to edit, without its name. */
export function startingPrompts(source: PlaygroundPrompts): PlaygroundPrompts {
  const sections: PlaygroundPrompts['sections'] = {};
  Object.entries(source.sections ?? {}).forEach(([name, kinds]) => {
    sections[name] = {};
    Object.entries(kinds ?? {}).forEach(([kind, rows]) => {
      sections[name][kind] = [...(rows ?? [])];
    });
  });
  return { version_prompts: { ...(source.version_prompts ?? {}) }, sections };
}

const withRows = (
  prompts: PlaygroundPrompts,
  section: string,
  kind: string,
  change: (rows: string[]) => string[]
): PlaygroundPrompts => {
  const current = prompts.sections[section] ?? {};
  return {
    ...prompts,
    sections: {
      ...prompts.sections,
      [section]: { ...current, [kind]: change([...(current[kind] ?? [])]) },
    },
  };
};

export const updateRow = (
  prompts: PlaygroundPrompts,
  section: string,
  kind: string,
  index: number,
  text: string
): PlaygroundPrompts =>
  withRows(prompts, section, kind, (rows) =>
    rows.map((row, i) => (i === index ? text : row))
  );

export const addRow = (
  prompts: PlaygroundPrompts,
  section: string,
  kind: string
): PlaygroundPrompts =>
  withRows(prompts, section, kind, (rows) => [...rows, '']);

export const removeRow = (
  prompts: PlaygroundPrompts,
  section: string,
  kind: string,
  index: number
): PlaygroundPrompts =>
  withRows(prompts, section, kind, (rows) =>
    rows.filter((_, i) => i !== index)
  );

export const updateVersionPrompt = (
  prompts: PlaygroundPrompts,
  key: string,
  text: string
): PlaygroundPrompts => ({
  ...prompts,
  version_prompts: { ...prompts.version_prompts, [key]: text },
});

/** Sections in the order the server lists them, then any others. */
export const sectionNames = (
  order: string[],
  prompts: PlaygroundPrompts
): string[] => {
  const present = Object.keys(prompts.sections ?? {});
  return [
    ...order.filter((name) => present.includes(name)),
    ...present.filter((name) => !order.includes(name)),
  ];
};

export interface RunForm {
  caseId: number;
  name: string;
  model: string;
  useStyleExample: boolean;
  prompts: PlaygroundPrompts;
}

/** The POST body: the full edited prompt set, never only the changes. */
export const buildRunBody = (form: RunForm): StartPlaygroundRun => ({
  case_id: form.caseId,
  name: form.name.trim(),
  writer_model: form.model,
  use_style_example: form.useStyleExample,
  prompts: startingPrompts(form.prompts),
});

/** Failed checks first, otherwise in the order the server sent them. */
export const orderedChecks = (checks: PlaygroundCheck[]): PlaygroundCheck[] => [
  ...checks.filter((c) => !c.passed),
  ...checks.filter((c) => c.passed),
];

const finite = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** Such as "$0.43"; empty when unknown. */
export const formatCost = (value: number | null | undefined): string =>
  finite(value) ? `$${value.toFixed(2)}` : '';

/** Such as "92 seconds"; empty when unknown. */
export const formatSeconds = (value: number | null | undefined): string =>
  finite(value) ? `${Math.round(value)} seconds` : '';

/** Such as "72.4"; a dash when unknown. */
export const formatScore = (value: number | null | undefined): string =>
  finite(value) ? `${Math.round(value * 10) / 10}` : '-';

const slug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/**
 * The prompts of a run as a file the demand letter test loop can apply
 * (backend demand_eval variants: it needs a name), named after the run.
 */
export function promptsFile(run: PlaygroundRun): {
  filename: string;
  json: string;
} {
  const tail = slug(run.name ?? '');
  const filename = `demand-prompts-run-${run.id}${tail ? `-${tail}` : ''}.json`;
  const body = {
    name: run.name?.trim() || `Run ${run.id}`,
    version_prompts: run.prompts?.version_prompts ?? {},
    sections: run.prompts?.sections ?? {},
  };
  return { filename, json: `${JSON.stringify(body, null, 2)}\n` };
}

/**
 * A letter section's text as paragraphs, split on blank lines. The backend
 * sends plain text; anything that looks like markup is shown as text (React
 * escapes it), never as HTML.
 */
export function letterBlocks(text: string): { paragraphs: string[] } {
  return {
    paragraphs: (text ?? '')
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean),
  };
}

/** Saves text as a file in the browser's downloads. */
export function saveTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked after the click has been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
